import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getLogRocketConfig } from 'AppSettings'
import { Authentication } from 'share/domain/authentication'
import { RefreshTokenResponseData } from 'share/queries/login'
import {
  getUnauthenticatedSessionId,
  clearUnauthenticatedSession
} from 'utils/unauthenticatedSession'

const initialState: Authentication = {
  isAuthenticated: false,
  idToken: null,
  accessToken: null,
  expirationTime: '',
  requiresMfa: false,
  sessionId: null,
  account: null
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pendo: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    LogRocket: any
  }
}

export function getEmptyAuthentication(): Authentication {
  const authentication: Authentication = {
    isAuthenticated: false,
    idToken: null,
    accessToken: null,
    expirationTime: '',
    requiresMfa: false,
    mfaDeliveryDestination: null,
    sessionId: null,
    account: {
      id: 0,
      givenName: '',
      middleName: '',
      suffix: '',
      familyName: '',
      homePhone: '',
      mobilePhone: '',
      workPhone: '',
      userName: '',
      emailAddress: '',
      street: '',
      zip: '',
      city: '',
      state: '',
      ssn: '',
      caseCount: 0,
      accountCreationStep: 1,
      contactType: 0
    }
  }

  return authentication
}

function initializePendo(authentication: Authentication) {
  if (authentication?.account?.id) {
    window.pendo.initialize({
      visitor: {
        id: authentication?.account?.emailAddress,
        first_name: authentication?.account?.givenName || '',
        last_name: authentication?.account?.familyName || '',
        state: authentication?.account?.state
      }
    })
  }
}

function initializeLogRocket(authentication: Authentication) {
  if (!window.LogRocket) {
    console.error(`Could not detect LogRocket on script load.`)
    return
  }
  const logRocketConfig = getLogRocketConfig()
  if (window.LogRocket) {
    const accountId = authentication?.account?.id || `accountIdNotDetected`

    console.log('Before initilize account id', authentication?.account?.id)

    if (authentication.isAuthenticated && authentication?.account?.id) {
      // LogRocket should already be initialized from index.tsx for unauthenticated tracking
      // If not initialized yet (edge case), initialize it now
      if (!window.LogRocket._isInitialized) {
        console.log('initializing logrocket (was not pre-initialized)')

        window.LogRocket.init(logRocketConfig.projectName, {
          dom: {
            inputSanitizer: logRocketConfig.inputSanitizer,
            textSanitizer: logRocketConfig.inputSanitizer
          }
        })
      }

      // Get the previous unauthenticated session ID for correlation
      const previousUnauthenticatedId = getUnauthenticatedSessionId()
      if (previousUnauthenticatedId) {
        console.log(
          'Upgrading LogRocket identity from unauthenticated session:',
          previousUnauthenticatedId,
          'to authenticated account:',
          accountId
        )
      } else {
        console.log(
          'Adding identifier to LogRocket session for account:',
          accountId
        )
      }

      // Upgrade identity from temporary ID to real user identity
      window.LogRocket.identify(accountId, {
        email: authentication?.account?.emailAddress,
        first_name: authentication?.account?.givenName || '',
        last_name: authentication?.account?.familyName || '',
        mobile_phone: authentication?.account?.mobilePhone || '',
        // Include the previous session ID for correlation in LogRocket dashboard
        previous_unauthenticated_session: previousUnauthenticatedId || 'none'
      })

      // Clear the temporary session ID now that user is authenticated
      clearUnauthenticatedSession()
    } else {
      console.log('LogRocket not updating identity - user not authenticated')
    }
  }
}

const authentication = createSlice({
  name: 'authentication',
  initialState: initialState,
  reducers: {
    setAuthentication(state, { payload }: PayloadAction<Authentication>) {
      setTimeout(() => {
        initializePendo(payload)
      }, 1000)
      return payload
    },
    setMobilePhone(state, { payload }: PayloadAction<string>) {
      if (state.account) {
        state.account.mobilePhone = payload
      }
    },
    setEmailAddress(state, { payload }: PayloadAction<string>) {
      if (state.account) {
        state.account.emailAddress = payload
      }
    },
    setIsAuthenticationFlag(state, { payload }: PayloadAction<boolean>) {
      state.isAuthenticated = payload
      initializeLogRocket(state)
    },
    setAccountCreationStep(state, { payload }: PayloadAction<number>) {
      if (state.account) {
        state.account.accountCreationStep = payload
      }
    },
    setWithRefreshTokenResults(
      state,
      { payload }: PayloadAction<RefreshTokenResponseData>
    ) {
      state.idToken = payload.idToken
      state.accessToken = payload.accessToken
      state.expirationTime = payload.expirationTime
    },
    incrementCaseCount(state) {
      if (state.account) {
        state.account.caseCount += 1
      }
    },
    decrementCaseCount(state) {
      if (state.account) {
        state.account.caseCount -= 1
      }
    }
  }
})

export const {
  setAuthentication,
  setMobilePhone,
  setEmailAddress,
  setIsAuthenticationFlag,
  setAccountCreationStep,
  setWithRefreshTokenResults,
  incrementCaseCount,
  decrementCaseCount
} = authentication.actions

export default authentication.reducer
